/**
 * Priority-scoped version totals for the Plan version list (STRY0038457).
 *
 * A suggestion carries one bug total and one CVE total and no per-priority
 * breakdown, so a scoped total is counted here from the version's own bug or
 * CVE list: the same lists, the same field and the same exact-value match the
 * detail view's filter uses. That is what makes a scoped total equal the row
 * count the reviewer gets by filtering the version's table to the same
 * priorities. Matching is deliberately not case-folded: the detail filter
 * offers `minor` and `Minor` as two options, so folding them here would count
 * rows that filter does not show.
 */

/**
 * The priority filter in `metadata.filters.bug` (or the severity filter in
 * `.cve`): the entry keyed or labelled severity/priority, else the first one.
 * Null when the vendor sends no filters at all.
 */
export function priorityFilterOf(filterMeta) {
  if (!Array.isArray(filterMeta) || filterMeta.length === 0) return null;
  const named = filterMeta.find(filter =>
    [filter?.key, filter?.label].some(name => ['severity', 'priority'].includes(String(name || '').toLowerCase()))
  );
  return named || filterMeta[0];
}

/**
 * `{value: count}` over `items[key]`. Items with no value are left out: no
 * selection matches them, exactly as the detail view's filter drops a bug with
 * no priority the moment any priority is selected.
 */
export function countByValue(items, key) {
  const counts = {};
  (Array.isArray(items) ? items : []).forEach(item => {
    const value = item?.[key];
    if (value === undefined || value === null || value === '') return;
    counts[value] = (counts[value] || 0) + 1;
  });
  return counts;
}

/** Sum of the selected values' counts, each value counted once. */
export function scopedTotal(counts, selected) {
  if (!counts || !Array.isArray(selected)) return 0;
  return [...new Set(selected)].reduce((sum, value) => sum + (counts[value] || 0), 0);
}

/**
 * Runs `fn` over `items`, at most `limit` at a time. Never rejects: a failed
 * item resolves to `{ error }`, so one version that cannot be fetched does not
 * stop the rest of the list from being counted.
 */
export async function mapWithLimit(items, limit, fn) {
  const results = new Array(items.length);
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const index = next;
      next += 1;
      try {
        // eslint-disable-next-line no-await-in-loop
        results[index] = await fn(items[index], index);
      } catch (error) {
        results[index] = { error };
      }
    }
  };
  await Promise.all(Array.from({ length: Math.max(1, Math.min(limit, items.length)) }, worker));
  return results;
}
